import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Button,
  CardBody,
  CardFooter,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import { blogService } from "../services/blogService";
import "../App.css";
import "../style/responsive.css";
import { useTranslation } from "react-i18next";

const Blog = ({
  title,
  slice,
  mode = "grid", // 'grid' or 'row'
  CARDS_PER_ROW = 4,
}) => {
  const { t } = useTranslation();
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      const cacheKey = "cachedBlogs";
      const cachedBlogs = localStorage.getItem(cacheKey);

      // Load cached data immediately if available
      if (cachedBlogs) {
        const parsed = JSON.parse(cachedBlogs);
        setBlogs(parsed);
        setIsLoading(false);
      }

      try {
        // Fetch new data from the server
        const data = await blogService.getAllBlogs();

        // Compare with cached data
        const newDataString = JSON.stringify(data);
        const cachedDataString = localStorage.getItem(cacheKey);

        if (newDataString !== cachedDataString) {
          setBlogs(data);
          localStorage.setItem(cacheKey, newDataString);
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Filter blogs
  const filteredBlogs = blogs.filter((blog) =>
    blog.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination logic
  const handleNext = () => {
    const cardsToShow = isMobile ? 1 : CARDS_PER_ROW;
    if (currentIndex + cardsToShow < filteredBlogs.length) {
      setCurrentIndex((prevIndex) => prevIndex + cardsToShow);
      setDirection(1);
    }
  };

  const handlePrevious = () => {
    const cardsToShow = isMobile ? 1 : CARDS_PER_ROW;
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => Math.max(0, prevIndex - cardsToShow));
      setDirection(-1);
    }
  };

  // Determine slices
  const startSlice = slice ? slice[0] : 0;
  const endSlice = slice ? slice[1] : filteredBlogs.length;

  const slicedBlogs = filteredBlogs.slice(startSlice, endSlice);

  // Adjust displayed blogs based on mode and screen size
  const getDisplayedBlogs = () => {
    if (mode === "grid") {
      return slicedBlogs;
    }

    if (isMobile) {
      // On mobile, show all blogs for horizontal scrolling
      return slicedBlogs;
    }

    // On desktop, show limited number with pagination
    const cardsToShow = CARDS_PER_ROW;
    return slicedBlogs.slice(currentIndex, currentIndex + cardsToShow);
  };

  const displayedBlogs = getDisplayedBlogs();

  const cardsToShow = isMobile ? 1 : CARDS_PER_ROW;
  const isNextDisabled = mode === "row" && !isMobile && currentIndex + cardsToShow >= slicedBlogs.length;
  const isPreviousDisabled = mode === "row" && !isMobile && currentIndex === 0;

  // Animations
  const cardVariants = {
    initial: (direction) => ({
      opacity: 0,
      x: direction > 0 ? 100 : -100,
      scale: 0.9,
    }),
    animate: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 20,
      },
    },
    exit: (direction) => ({
      opacity: 0,
      x: direction > 0 ? -100 : 100,
      scale: 0.9,
      transition: {
        duration: 0.5,
      },
    }),
  };

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <section className="Blog" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
      <Container className="my-md-5 my-0">
        {/* Title and Navigation for Row Mode */}
        {mode === "row" && (
          <div className="title d-flex justify-content-center align-items-center py-md-5 mt-5">
            <div className="title d-flex align-items-center justify-content-center flex-column mb-2">
              <h4 className="blog-title fs-2 ">{title || t("latest_blogs")}</h4>
              <div className="blog-line-style"></div>
            </div>

            {/* Show navigation buttons on desktop, hide on mobile */}
            {/* {!isMobile && (
              <div className="d-flex mb-2 justify-content-center align-items-center gap-2">
                <div className="previous-style d-flex justify-content-center align-items-center rounded-5">
                  <button
                    className="bg-transparent border-0"
                    onClick={handlePrevious}
                    disabled={isPreviousDisabled}
                    style={{
                      opacity: isPreviousDisabled ? 0.5 : 1,
                      cursor: isPreviousDisabled ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <FaAngleLeft className="fs-5" />
                  </button>
                </div>
                <div className="previous-style d-flex justify-content-center align-items-center rounded-5">
                  <button
                    className="bg-transparent border-0"
                    onClick={handleNext}
                    disabled={isNextDisabled}
                    style={{
                      opacity: isNextDisabled ? 0.5 : 1,
                      cursor: isNextDisabled ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <FaAngleRight className="fs-5" />
                  </button>
                </div>
              </div>
            )} */}
          </div>
        )}

        {/* Title for Grid Mode */}
{mode === "grid" && (
  <div className="title py-0 py-md-5 d-flex justify-content-center align-items-center flex-column">
    <div className="title d-flex align-items-center justify-content-center flex-column pb-md-5 mt-5">
      <h4 className="shop-search-title text-center">
        {title || t("latest_blogs")}
      </h4>
      <div className="title-line-style mt-2 mt-md-0"></div>
    </div>

    <div
      className="d-flex seach-component align-items-center position-relative mt-top"
      style={{
        width: "100%",
        maxWidth: "100%",
      }}
    >
      <input
        type="search"
        className="form-control product-search shadow-sm py-3"
        placeholder={t("search_blogs")}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{
          width: "100%",
          maxWidth: "100%",
          minWidth: "100%",
        }}
      />
    </div>
  </div>
)}

        <AnimatePresence>
          {mode === "grid" ? (
            <Row className="mb-5">
              {displayedBlogs.map((blog) => (
                <Col
                  xs={12}
                  md={6}
                  lg={3}
                  key={blog._id}
                  className="mt-5-custom "
                >
                  <motion.div
                    variants={cardVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    <BlogCard blog={blog} t={t} />
                  </motion.div>
                </Col>
              ))}
            </Row>
          ) : (
            <div className={`blog-row-container ${isMobile ? 'mobile-scroll' : ''}`}>
              <Row className={`g-4 mt-md-4 mt-1 ${isMobile ? 'flex-nowrap overflow-auto pb-3' : ''}`}>
                {displayedBlogs.map((blog) => (
                  <Col
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    key={blog._id}
                    className={isMobile ? 'flex-shrink-0' : ''}
                    style={isMobile ? { width: '85%' } : {}}
                  >
                    <motion.div
                      custom={direction}
                      variants={cardVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                    >
                      <BlogCard blog={blog} t={t} />
                    </motion.div>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </AnimatePresence>
      </Container>
    </section>
  );
};

// BlogCard remains the same
const BlogCard = ({ blog, t }) => (
  <Card className="border-0 bg-white overflow-hidden h-100 ">
    <div className="blog-image-size">
      <Link to={`/blog/${blog._id}`} className="text-decoration-none">
        <Card.Img
          variant="top"
          src={blog.image}
          alt="Blog Image"
          className="Image"
        />
      </Link>
    </div>
    <CardBody className="px-2 py-2 lh-1">
      <div className="d-flex gap-1">
        <Card.Text className="opacity-50">{blog.date} - </Card.Text>
        <span className="opacity-50">Healthy Tip By Dr.</span>
      </div>
      <Card.Title className="text-color title-indent">
        <h6 className="text-blog-hover">{blog.title}</h6>
      </Card.Title>
    </CardBody>
    <CardFooter className="p-0 w-100 border-0 bg-white mt-3">
      <Link to={`/blog/${blog._id}`} className="text-decoration-none w-100">
        <Button
          variant="primary"
          className="w-100 btn-card-hover btn-color border-0 py-2 rounded-5"
        >
          {t("Read More")}
        </Button>
      </Link>
    </CardFooter>
  </Card>
);

export default Blog;
